import { useContext, useEffect, useState } from "react";
import Modal from "react-modal";
import { sendLogsToApi } from "../../utils/consoleCapture";
import { captureWholeAppScreenshot } from "../../utils/screenshotUtils";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import Loader from "../genericComponents/Loader/Loader";
import CloseIcon from "../../assets/close.svg?react";
import { UserContext } from "../../Context";
import "./ErrorReporter.css";

interface ErrorReporterProps {
    isOpen: boolean;
    onClose: () => void;
}

const ErrorReporter = ({ isOpen, onClose }: ErrorReporterProps) => {
    const [isVisible, setIsVisible] = useState(isOpen);
    const [screenshot, setScreenshot] = useState<string | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [referenceId, setReferenceId] = useState<string | null>(null);
    const [sendFailed, setSendFailed] = useState(false);
    const [userInfo, setUserInfo] = useState("");

    const userContext = useContext(UserContext);
    const userName = userContext?.user.value?.fullName ?? "Ukjent";

    // Sync visibility and refresh logs when the modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, [isOpen]);

    const handleClose = () => {
        setReferenceId(null);
        setSendFailed(false);
        setScreenshot(null);
        setUserInfo("");
        setIsVisible(false);
        onClose();
    };

    const handleSend = async () => {
        const newReferenceId = crypto.randomUUID().slice(0, 8).toLowerCase();
        setIsSending(true);
        setSendFailed(false);
        try {
            const { failed } = await sendLogsToApi(
                userName,
                newReferenceId,
                screenshot ?? undefined,
                userInfo.trim() || undefined
            );
            if (failed === 0) {
                setReferenceId(newReferenceId);
            } else {
                setSendFailed(true);
            }
        } catch {
            setSendFailed(true);
        } finally {
            setIsSending(false);
        }
    };

    const handleCaptureScreenshot = async () => {
        setIsCapturing(true);
        // Close the modal so the app is fully visible for the screenshot
        setIsVisible(false);
        // Wait for the modal close animation to finish
        await new Promise<void>((resolve) => setTimeout(resolve, 400));
        try {
            const dataUrl = await captureWholeAppScreenshot();
            if (dataUrl) setScreenshot(dataUrl);
        } catch {
            // User cancelled or denied permission — that's fine
        } finally {
            setIsCapturing(false);
            setIsVisible(true);
        }
    };

    return (
        <Modal
            isOpen={isVisible}
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            onRequestClose={handleClose}
            overlayClassName="error-reporter-overlay"
            className="error-reporter-content"
        >
            <div className="error-reporter-modal">
                <div className="error-reporter-header">
                    <div className="error-reporter-header-top">
                        <h3 className="error-reporter-title">Feilrapportering</h3>
                        <button type="button" className="empty-button" onClick={handleClose}>
                            <CloseIcon />
                        </button>
                    </div>
                    {!referenceId && (
                        <p className="error-reporter-description">
                            Ta en skjermdump, skriv litt om saken, og send det til support.
                            <br />
                            (Det også sendes logger fra din nettleser.)
                        </p>
                    )}
                </div>
                {isSending && <Loader inline size={32} />}

                {referenceId ? (
                    <div className="error-reporter-confirmation">
                        <p className="error-reporter-confirmation-text">Rapporten er sendt til support.</p>
                        <p className="error-reporter-confirmation-text">
                            Referanse-ID: <strong className="error-reporter-reference-id">{referenceId}</strong>
                        </p>
                        <p className="error-reporter-confirmation-hint">Oppgi denne ID-en når du kontakter support.</p>
                        <NfsButton onClick={handleClose}>Lukk</NfsButton>
                    </div>
                ) : (
                    <>
                        <textarea
                            className="error-reporter-userinfo"
                            placeholder="Beskriv hva som skjedde (valgfritt)"
                            value={userInfo}
                            onChange={(e) => setUserInfo(e.target.value)}
                            rows={3}
                            disabled={isSending}
                        />
                        {screenshot && (
                            <div className="error-reporter-screenshot">
                                <div className="error-reporter-screenshot-header">
                                    <span>Skjermbilde vedlagt</span>
                                    <button
                                        className="error-reporter-screenshot-remove"
                                        onClick={() => setScreenshot(null)}
                                    >
                                        Fjern
                                    </button>
                                </div>
                                <img src={screenshot} alt="Skjermbilde" className="error-reporter-screenshot-preview" />
                            </div>
                        )}
                        <div className="error-reporter-actions">
                            <NfsButton outlined onClick={handleCaptureScreenshot} disabled={isCapturing || isSending}>
                                {screenshot ? "Ta nytt skjermbilde" : "Legg ved skjermbilde"}
                            </NfsButton>
                            <NfsButton onClick={handleSend} disabled={isSending}>
                                Send til support
                            </NfsButton>
                        </div>
                        <p className="error-reporter-screenshot-hint">
                            Skjermbilde: dette vinduet lukkes midlertidig mens du godkjenner og velger hva som skal
                            deles i nettleserens dialog.
                        </p>
                        {sendFailed && <p className="error-reporter-send-result">⚠ Sending feilet. Prøv igjen.</p>}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ErrorReporter;
